/**
 * SCWT-PM-V2 Phase 4 · Feb 2026 — public landlord pay page.
 * Route: /pay-for-water-test/:token
 *
 * The single external landing surface for a PM-assisted booking. The
 * token in the URL is the AWARE pay-link token (7-day). Stripe
 * checkout sessions are minted on demand — this page NEVER contains a
 * Stripe URL. Recipients set report-sharing consent BEFORE we redirect
 * to Stripe.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ShieldCheck, Building2, CheckCircle2, XCircle, Loader2, Clock, MapPin,
  Info, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

const DECLINE_REASONS = [
  { key: "do_not_want",         label: "I do not want to proceed" },
  { key: "not_my_property",     label: "This is not my property" },
  { key: "contact_someone_else", label: "The property manager should contact someone else" },
  { key: "already_arranged",    label: "I have already arranged testing" },
  { key: "other",               label: "Other" },
];

export default function PayForWaterTest() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const paidReturn = search.get("paid") === "1";

  const [loading, setLoading]         = useState(true);
  const [payload, setPayload]         = useState(null);
  const [terminalState, setTerminalState] = useState(null);
  const [error, setError]             = useState(null);
  const [status, setStatus]           = useState(null);   // polled if paid=1

  // consent
  const [consent, setConsent] = useState({
    share_with_pm: true,
    share_corrective_with_service_partner: false,
    keep_full_report_private_to_payer: false,
  });
  const [consentSaved, setConsentSaved] = useState(false);
  const [consentBusy, setConsentBusy]   = useState(false);

  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [declineOpen, setDeclineOpen]   = useState(false);
  const [declineReason, setDeclineReason] = useState("do_not_want");
  const [declineMessage, setDeclineMessage] = useState("");
  const [declineBusy, setDeclineBusy]   = useState(false);
  const [declined, setDeclined]         = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/pm-payment-requests/${token}`);
        if (cancelled) return;
        if (data.terminal_state) {
          setTerminalState(data.terminal_state);
        } else {
          setPayload(data);
          const d = data.report_permissions_default || {};
          setConsent({
            share_with_pm: d.share_with_pm ?? true,
            share_corrective_with_service_partner: d.share_corrective_with_service_partner ?? false,
            keep_full_report_private_to_payer:  d.keep_full_report_private_to_payer ?? false,
          });
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "unavailable");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!paidReturn) return;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/pm-payment-requests/${token}/status`);
        if (stopped) return;
        setStatus(data);
        if (["converted_to_booking", "conversion_failed"].includes(data.status)) return;
        setTimeout(poll, 3000);
      } catch { /* silent */ }
    };
    poll();
    return () => { stopped = true; };
  }, [paidReturn, token]);

  const saveConsent = async () => {
    setConsentBusy(true);
    try {
      await api.post(`/public/pm-payment-requests/${token}/consent`, consent);
      setConsentSaved(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save preferences");
    } finally { setConsentBusy(false); }
  };

  const continueToStripe = async () => {
    if (!consentSaved) { await saveConsent(); }
    setCheckoutBusy(true); setCheckoutError(null);
    try {
      const { data } = await api.post(`/public/pm-payment-requests/${token}/checkout`, {});
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
      } else {
        setCheckoutError("No checkout URL returned. Please try again.");
      }
    } catch (e) {
      setCheckoutError(e?.response?.data?.detail || "Could not open checkout");
    } finally { setCheckoutBusy(false); }
  };

  const submitDecline = async () => {
    setDeclineBusy(true);
    try {
      await api.post(`/public/pm-payment-requests/${token}/decline`, {
        reason: declineReason,
        message: declineMessage || undefined,
      });
      setDeclined(true);
    } catch { /* show inline */ }
    finally { setDeclineBusy(false); }
  };

  if (loading) return <FullPage><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></FullPage>;

  if (error === "payment_request_expired" || terminalState === "expired") {
    return <Terminal icon={<Clock />} title="This payment request has expired"
                      body="Please contact your Property Manager for a fresh link." />;
  }
  if (terminalState === "declined" || declined) {
    return <Terminal icon={<XCircle />} title="Request declined"
                      body="We've let the property manager know. Thanks for your response." />;
  }
  if (terminalState === "cancelled") {
    return <Terminal icon={<XCircle />} title="Request cancelled"
                      body="The property manager cancelled this request." />;
  }
  if (terminalState === "converted_to_booking" || status?.status === "converted_to_booking") {
    return <Terminal icon={<CheckCircle2 />} title="Payment received"
                      body={`Your booking is confirmed${status?.booking_reference ? ` — reference ${status.booking_reference}` : ""}.`}
                      tone="ok" />;
  }
  if (paidReturn && status && ["payment_received", "conversion_pending"].includes(status.status)) {
    return <FullPage><div className="text-center">
      <Loader2 className="w-6 h-6 animate-spin text-cyan-700 mx-auto" />
      <p className="mt-3 text-sm text-slate-600">Confirming your payment…</p>
    </div></FullPage>;
  }

  if (error) {
    return <Terminal icon={<AlertTriangle />} title="This link is no longer available"
                      body="The invitation may already be used, expired, or was withdrawn." />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <div className="text-xs text-slate-500">Water testing payment</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Participants + property */}
        <section data-testid="paylink-participants" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
            <ShieldCheck className="w-3.5 h-3.5" /> AWARE Water Testing
          </div>
          <h1 data-testid="paylink-h1" className="mt-3 text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
            {payload?.agency?.name} has invited you to arrange water testing
            for <span data-testid="paylink-property">{payload?.property_address}</span>.
          </h1>
          {payload?.local_partner && (
            <p className="mt-3 text-sm text-slate-600">
              Local support for this property is provided by
              &nbsp;<strong data-testid="paylink-partner">{payload.local_partner.name}</strong>.
            </p>
          )}
          {payload?.optional_pm_message && (
            <div data-testid="paylink-pm-message" className="mt-5 rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900">
              <div className="text-[11px] uppercase font-bold text-blue-700 mb-1 inline-flex items-center gap-1"><Info className="w-3 h-3" /> Message from {payload.agency.name}</div>
              {payload.optional_pm_message}
            </div>
          )}
        </section>

        {/* Test + amount */}
        <section data-testid="paylink-test" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Selected test</div>
              <div data-testid="paylink-test-name" className="mt-1 text-lg font-bold text-slate-900">{payload?.test?.name}</div>
              {payload?.test?.kit_required && (
                <div className="text-xs text-slate-500 mt-1">Sample kit posted to the property address on successful payment.</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total</div>
              <div data-testid="paylink-amount" className="text-3xl font-bold text-cyan-700">{payload?.amount_display}</div>
            </div>
          </div>
        </section>

        {/* Consent / report sharing */}
        <section data-testid="paylink-consent" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="text-lg font-bold text-slate-900">Report sharing</h2>
          <p className="text-xs text-slate-500 mt-1">
            You control who sees your report. Confirm below before continuing to payment.
          </p>
          <div className="mt-4 space-y-3">
            <ConsentRow label={`Share the final report with ${payload?.agency?.name || "the property manager"}`}
                         value={consent.share_with_pm}
                         onChange={(v) => { setConsent((c) => ({ ...c, share_with_pm: v })); setConsentSaved(false); }}
                         testid="consent-share-pm" />
            <ConsentRow label={`Share corrective-action recommendations with ${payload?.local_partner?.name || "your local PWS Partner"}`}
                         value={consent.share_corrective_with_service_partner}
                         onChange={(v) => { setConsent((c) => ({ ...c, share_corrective_with_service_partner: v })); setConsentSaved(false); }}
                         testid="consent-share-partner" />
            <ConsentRow label="Keep the full report private to me (I control who sees the details)"
                         value={consent.keep_full_report_private_to_payer}
                         onChange={(v) => { setConsent((c) => ({ ...c, keep_full_report_private_to_payer: v })); setConsentSaved(false); }}
                         testid="consent-keep-private" />
          </div>
        </section>

        {checkoutError && (
          <div data-testid="paylink-checkout-error" className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-sm text-rose-700">
            {checkoutError}
          </div>
        )}

        {/* Actions */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div className="flex flex-wrap gap-3 items-center">
            <button
              data-testid="paylink-continue"
              disabled={checkoutBusy || consentBusy}
              onClick={continueToStripe}
              className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 disabled:bg-slate-300 text-white font-semibold rounded-full px-6 py-3 text-sm">
              {(checkoutBusy || consentBusy) && <Loader2 className="w-4 h-4 animate-spin" />}
              Continue to payment →
            </button>
            <button
              data-testid="paylink-decline"
              onClick={() => setDeclineOpen(true)}
              className="text-sm text-slate-500 hover:text-slate-800">
              I don't want to proceed
            </button>
          </div>
          <div className="mt-4 text-[11px] text-slate-500">
            AWARE will only charge the amount shown above. Payment is processed by Stripe.
            This link expires on {payload?.expires_at?.slice(0, 10)}.
          </div>
        </section>

        {declineOpen && (
          <div data-testid="paylink-decline-dialog" className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-slate-900">Let the property manager know</h3>
              <div className="mt-4 space-y-2">
                {DECLINE_REASONS.map((r) => (
                  <label key={r.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="radio"
                            data-testid={`paylink-decline-${r.key}`}
                            checked={declineReason === r.key}
                            onChange={() => setDeclineReason(r.key)} />
                    {r.label}
                  </label>
                ))}
              </div>
              <textarea
                data-testid="paylink-decline-message"
                value={declineMessage}
                onChange={(e) => setDeclineMessage(e.target.value)}
                placeholder="Optional message"
                className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                rows={3}
              />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setDeclineOpen(false)}
                        data-testid="paylink-decline-cancel"
                        className="text-sm text-slate-600 hover:text-slate-800">Cancel</button>
                <button onClick={submitDecline}
                        disabled={declineBusy}
                        data-testid="paylink-decline-submit"
                        className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-full px-4 py-2">
                  {declineBusy ? "Sending…" : "Send decline"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function FullPage({ children }) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">{children}</div>;
}

function Terminal({ icon, title, body, tone = "info" }) {
  const toneCls = tone === "ok" ? "border-emerald-100" : "border-slate-200";
  const iconCls = tone === "ok" ? "text-emerald-600" : "text-slate-400";
  return (
    <FullPage>
      <div data-testid="paylink-terminal" className={`bg-white rounded-2xl border ${toneCls} p-8 text-center max-w-md`}>
        <div className={`w-14 h-14 rounded-full mx-auto flex items-center justify-center ${iconCls}`}>
          {icon}
        </div>
        <h1 className="text-xl font-bold text-slate-900 mt-4">{title}</h1>
        <p className="text-slate-600 mt-2 text-sm">{body}</p>
      </div>
    </FullPage>
  );
}

function ConsentRow({ label, value, onChange, testid }) {
  return (
    <label className="flex gap-3 items-start text-sm text-slate-700 cursor-pointer">
      <input type="checkbox"
              data-testid={testid}
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              className="mt-0.5 h-4 w-4" />
      <span>{label}</span>
    </label>
  );
}
